import { useCallback, useEffect, useRef, useState } from 'react';
import { restoreRange } from './useAnchorResolver';
import type { Annotation, HighlightRect } from '../HtmlViewerTypes';

function computeRects(
  annotations: Annotation[],
  contentRef: React.RefObject<HTMLDivElement>,
  geometryRootRef: React.RefObject<HTMLDivElement>
): HighlightRect[] {
  const contentRoot = contentRef.current;
  const geometryRoot = geometryRootRef.current;
  if (!contentRoot || !geometryRoot) return [];

  const geometryRect = geometryRoot.getBoundingClientRect();

  const rects: HighlightRect[] = [];

  for (const annotation of annotations) {
    const range = restoreRange(annotation.anchor, contentRoot);

    if (!range) {
      // Annotation is detached — skip rendering
      continue;
    }

    const clientRects = range.getClientRects();

    for (let i = 0; i < clientRects.length; i++) {
      const r = clientRects[i];
      if (r.width < 1 || r.height < 1) continue;

      rects.push({
        x: r.left - geometryRect.left,
        y: r.top - geometryRect.top,
        width: r.width,
        height: r.height,
        annotationId: annotation.id,
        color: annotation.color,
      });
    }
  }

  return rects;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useHighlightRects(
  annotations: Annotation[],
  contentRef: React.RefObject<HTMLDivElement>,
  geometryRootRef: React.RefObject<HTMLDivElement>
): HighlightRect[] {
  const [rects, setRects] = useState<HighlightRect[]>([]);
  const rafRef = useRef<number | null>(null);

  const scheduleRecompute = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      setRects(computeRects(annotations, contentRef, geometryRootRef));
      rafRef.current = null;
    });
  }, [annotations, contentRef, geometryRootRef]);

  // Recompute when annotations change
  useEffect(() => {
    scheduleRecompute();
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [scheduleRecompute]);

  // Recompute on resize
  useEffect(() => {
    const contentRoot = contentRef.current;
    if (!contentRoot) return;

    const observer = new ResizeObserver(() => {
      scheduleRecompute();
    });
    observer.observe(contentRoot);

    const geometryRoot = geometryRootRef.current;
    if (geometryRoot && geometryRoot !== contentRoot) {
      observer.observe(geometryRoot);
    }

    return () => observer.disconnect();
  }, [contentRef, geometryRootRef, scheduleRecompute]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => scheduleRecompute();
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [scheduleRecompute]);

  return rects;
}
